import { useAuth } from '@/app/context';
import { getRoleLabel } from '@/shared/utils/get-role-label';
import {
  getScopeFromGroupName,
} from '@/shared/utils/get-group';
import { Avatar, Button, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, LogOut } from 'lucide-react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

export const UserProfileMenu = memo(function UserProfileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { logout, attributes, groups, loading } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    await logout();
    navigate({ to: '/login' });
  }, [logout, navigate]);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
  }, []);

  const closeMenu = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => setIsOpen(false), 200);
  }, []);

  const toggleMenu = useCallback(() => {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [isOpen, openMenu, closeMenu]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeMenu]);

  // Get user attributes from AWS Cognito
  const firstName = attributes?.given_name || '';
  const lastName = attributes?.family_name || '';
  const userEmail = attributes?.email || '';
  const cognitoName = attributes?.name || '';

  // Build full name: prefer name, then given_name + family_name, then email as fallback
  // Avoid showing "User" while still loading attributes
  const fullName =
    cognitoName ||
    (firstName && lastName ? `${firstName} ${lastName}` : '') ||
    firstName ||
    lastName ||
    userEmail?.split('@')[0] ||
    (loading && !attributes ? '' : 'User');

  const groupName = groups?.[0] ?? '';
  const scopeLabel = getScopeFromGroupName(groupName);
  const roleLabel = getRoleLabel(groupName);
  const scopeAndProfile =
    scopeLabel !== '-' && roleLabel
      ? `${scopeLabel} | ${roleLabel}`
      : scopeLabel !== '-'
        ? scopeLabel
        : roleLabel || '';

  // Get initials from fullName (first letter of first + last word)
  const getInitials = () => {
    const name = fullName.trim();
    if (!name) return 'U';
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  return (
    <div ref={menuRef} className='relative'>
      {/* Profile Button */}
      <Button
        variant='ghost'
        size='small'
        onClick={toggleMenu}
        className='flex min-w-0 max-w-[2.75rem] items-center gap-1 rounded-full px-1 py-1 hover:bg-alba-blue-500 sm:max-w-[16rem] sm:gap-2 sm:px-2 md:max-w-[20rem]'
        aria-haspopup='menu'
        aria-expanded={isOpen}
      >
        <Avatar.Root
          size='sm'
          className='h-8 w-8 flex-shrink-0 bg-alba-blue-400 hover:bg-alba-blue-200 transition-colors rounded-full'
        >
          <Avatar.Fallback className='text-sm font-semibold text-white'>
            {getInitials()}
          </Avatar.Fallback>
        </Avatar.Root>
        <span className='hidden min-w-0 flex-col items-start leading-tight sm:flex'>
          {fullName ? (
            <span className='w-full truncate text-white text-sm font-medium block'>
              {fullName}
            </span>
          ) : (
            <span
              className='w-16 h-4 rounded bg-alba-blue-400/60 animate-pulse block'
              aria-hidden
            />
          )}
        </span>
        <ChevronDown
          className='hidden h-4 w-4 flex-shrink-0 text-white sm:block'
          strokeWidth={1.6}
        />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute right-0 top-[4em] w-72 p-3 rounded-lg bg-white shadow-md shadow-gray-50 border border-zinc-200 overflow-hidden transition-all duration-200 ease-out ${
            isAnimating
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-2'
          }`}
        >
          {/* User Info Header */}
          <div className='p-4 border-b border-zinc-200'>
            <div className='flex items-center gap-3'>
              {/* Avatar */}
              <Avatar.Root className='w-12 h-12 bg-gradient-to-br from-alba-blue-500 to-alba-blue-600 flex-shrink-0'>
                <Avatar.Fallback className='text-base font-bold text-white'>
                  {getInitials()}
                </Avatar.Fallback>
              </Avatar.Root>

              {/* User Details */}
              <div className='flex-1 min-w-0'>
                <Text className='text-sm font-semibold text-grey-900 truncate'>
                  {fullName}
                </Text>
                {scopeAndProfile ? (
                  <Text className='text-xs text-grey-600 truncate'>
                    {scopeAndProfile}
                  </Text>
                ) : null}
                {userEmail && (
                  <span
                    className='text-xs text-grey-500 truncate block'
                    title={userEmail}
                  >
                    {userEmail}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className='p-2 space-y-1'>
            {/* Logout */}
            <Button
              variant='ghost'
              size='small'
              onClick={handleLogout}
              className='w-full justify-start gap-3 px-3 py-2.5 text-red-600 hover:bg-red-50 hover:text-red-700'
            >
              <LogOut className='w-5 h-5 flex-shrink-0' strokeWidth={1.6} />
              <span className='text-sm font-medium'>Logout</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
